<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 mt-5">
        <!-- Page Title -->
        <TableTitle
          title="Knowledge Categories"
          pageTitle="Knowledge Categories"
          icon="fas fa-book"
          :showAction="true"
          actionLabel="Create Category"
          @action="openCreateModal"
          :disabled="!canCreate('website')"
        />

        <!-- Table Wrapper -->
        <div class="table_style">
          <CommonTable2
            :tableObj="tableConfig"
            @edit="editCategory"
            @delete="deleteCategory"
            @view="viewCategory"
            @toggleFeatured="toggleFeatured" 
            @statusFilter="handleStatusFilter"
            @featuredFilter="handleFeaturedFilter" 
            @toggle-status="toggleStatus"
            @resetFilters="resetFilters"
            permissionKey="website"
          >          
          </CommonTable2>
        </div>

        <!-- Pagination -->
        <div
          v-if="tablePagination.total_pages >= 1"
          class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4"
        >
          <!-- Rows per page -->
          <div class="d-flex align-items-center gap-2">
            <span class="text-muted">Rows per page:</span>
            <select
              class="form-select form-select-sm rows-select"
              v-model="tablePagination.per_page"
              @change="changeRows"
            >
              <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
                {{ n }}
              </option>
            </select>
          </div>

          <!-- Pagination Buttons -->
          <div class="pagination-mini">
            <button
              class="page-btn prev_btn"
              :disabled="!tablePagination.has_prev"
              @click="handlePageChange(tablePagination.current_page - 1)"
            >
              «
            </button>

            <button
              v-for="(p, index) in visiblePages"
              :key="index"
              class="page-btn"
              :class="{
                'page-active': p === tablePagination.current_page,
                'disabled-dots': p === '...',
              }"
              :disabled="p === '...'"
              @click="p !== '...' && handlePageChange(p)"
            >
              {{ p }}
            </button>

            <button
              class="page-btn next_btn"
              :disabled="!tablePagination.has_next"
              @click="handlePageChange(tablePagination.current_page + 1)"
            >
              »
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <KnowledgeCategoryModal
      v-if="showModal"
      :show="showModal"
      :category="selectedCategory"
      :is-edit="isEditMode"
      @close="closeModal"
      @saved="handleCategorySaved"
    />

    <!-- View Modal -->
    <KnowledgeCategoryViewModal
      v-if="showViewModal"
      :show="showViewModal"
      :category="selectedCategory"
      @close="closeViewModal"
    />
  </div>
</template>
<script setup lang="ts">
import { ref, reactive, computed, onMounted, defineAsyncComponent } from "vue";
import apiService from "@/services/apiService";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { toast } from "vue3-toastify";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const KnowledgeCategoryModal = defineAsyncComponent(
  () => import("@/components/theme/admin/knowledge/KnowledgeCategoryModal.vue")
);
const KnowledgeCategoryViewModal = defineAsyncComponent(
  () =>
    import("@/components/theme/admin/knowledge/KnowledgeCategoryViewModal.vue")
);

// State
const categories = ref([]);
const loading = ref(false);
const filters = reactive({
  status: "",
  featured: Boolean,
});

// Pagination
const tablePagination = reactive({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_prev: false,
  has_next: false,
});

// Dynamic Pages
const visiblePages = computed(() => {
  const total = tablePagination.total_pages;
  const current = tablePagination.current_page;

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let pages = [1];

  if (current > 4) pages.push("...");
  for (
    let i = Math.max(2, current - 1);
    i <= Math.min(total - 1, current + 1);
    i++
  ) {
    pages.push(i);
  }
  if (current < total - 3) pages.push("...");
  pages.push(total);

  return pages;
});

// Fetch Data
const fetchCategories = async () => {
  loading.value = true;
  const res = await apiService.getKnowledgeCategories({
    page: tablePagination.current_page,
    limit: tablePagination.per_page,
    status: filters.status,
    featured: filters.featured,
  });

  // categories.value = res.data;
  categories.value = res.data.map((item: any) => ({
    _id: item._id,
    icon: item.icon,
    title: item.title,
    slug: item.slug,
    article_count: item.blog_count,
    created_at: item.created_at
      ? new Date(item.created_at).toLocaleDateString()
      : "",
    sort_order: item.sort_order,
    is_featured: item.is_featured,
    is_active: item.is_active
  }));
  console.log(categories.value, "all categories");
  tablePagination.total = res.pagination.total;
  tablePagination.total_pages = Math.ceil(res.total / tablePagination.per_page);
  tablePagination.has_prev = tablePagination.current_page > 1;
  tablePagination.has_next =
    tablePagination.current_page < tablePagination.total_pages;

  loading.value = false;
};

// Table Config
const tableConfig = ref({
  loading: loading,
  data: categories,
  columns: [
    { name: "Icon", field: "icon", width: "auto" },
    { name: "Title", field: "title", sortable: true, width: "auto" },
    { name: "Slug", field: "slug", type: "code", width: "auto" },
    { name: "Articles", field: "article_count", type: "badge", width: "auto" },
    {
      name: "Featured",
      field: "featured",
      type: "toggle_feature",
      width: "auto",
    },
    {
      name: "Status",
      field: "is_active",
      type: "toggle_status",
      width: "auto",
    },
    { name: "Sort Order", field: "sort_order", type: "badge", width: "auto" },
    { name: "Created", field: "created_at", type: "date", width: "auto" },
  ],
  actions: [true],
  showStatusFilter: true,
  allStatusLables: ["Active", "Inactive"],
  categories: ["Featured", "Regular"],
  showFeaturedFilter: true,
  showResetFilter: true,
});

// Pagination handlers
const handlePageChange = (page: number) => {
  tablePagination.current_page = page;
  fetchCategories();
};

const changeRows = () => {
  tablePagination.current_page = 1;
  fetchCategories();
};

// Filters
const handleStatusFilter = (value: string) => {
  console.log(value, "this is status") 
     filters.status = value.toLowerCase();
     fetchCategories();
};

const handleFeaturedFilter = (value: string) => {
  const val = value.toLowerCase();

  filters.featured =
    val === "featured" ? true :
    val === "regular" ? false :
    "";
  fetchCategories();
};

const resetFilters = () => {
  filters.status = "";
  filters.featured = "";
  tablePagination.current_page = 1;
  fetchCategories();
};


// CRUD Actions
const editCategory = (cat) => {
  selectedCategory.value = cat;
  isEditMode.value = true;
  showModal.value = true;
};

const viewCategory = (cat) => {
  selectedCategory.value = cat;
  showViewModal.value = true;
};

const deleteCategory = async (id) => {
  await apiService.deleteKnowledgeCategory(id);
  fetchCategories();
  toast.success("Category deleted");
};

const toggleStatus = async (id: string, status: boolean) => {
  try {
    // Optimistic update - update UI immediately
    const categoryIndex = categories.value.findIndex((cat) => cat._id === id);
    console.log(categoryIndex, "finded category");
    if (categoryIndex !== -1) {
      categories.value[categoryIndex].is_active = status;
    }

    await apiService.toggleKnowledgeCategoryStatus(id, status);
    toast.success(
      `Category ${status ? "activated" : "deactivated"} successfully`
    );

    // Refresh data to ensure consistency
    await fetchCategories();
  } catch (error) {
    console.error("Error toggling status:", error);
    toast.error("Failed to update category status");

    // Revert optimistic update on error
    const categoryIndex = categories.value.findIndex((cat) => cat._id === id);
    if (categoryIndex !== -1) {
      categories.value[categoryIndex].is_active = !status;
    }
  }
};

const toggleFeatured = async (id: string) => {
  try {
    // Optimistic update - update UI immediately
    const categoryIndex = categories.value.findIndex((cat) => cat._id === id);
    if (categoryIndex !== -1) {
      categories.value[categoryIndex].is_featured = !categories.value[categoryIndex].is_featured;
    }

    await apiService.toggleKnowledgeCategoryFeatured(id, categories.value[categoryIndex].is_featured);
    toast.success(
      `Category ${categories.value[categoryIndex].is_featured ? "marked as featured" : "removed from featured"}`
    );

    // Refresh data to ensure consistency
    await fetchCategories();
  } catch (error) {
    console.error("Error toggling featured:", error);
    toast.error("Failed to update featured status");

    // Revert optimistic update on error
    const categoryIndex = categories.value.findIndex((cat) => cat._id === id);
    if (categoryIndex !== -1) {
      categories.value[categoryIndex].is_featured = !featured;
    }
  }
};

// Modals
const showModal = ref(false);
const showViewModal = ref(false);
const selectedCategory = ref(null);
const isEditMode = ref(false);

const openCreateModal = () => {
  selectedCategory.value = null;
  isEditMode.value = false;
  showModal.value = true;
};

const closeModal = () => (showModal.value = false);
const closeViewModal = () => (showViewModal.value = false);

const handleCategorySaved = () => {
  fetchCategories();
  closeModal();
  toast.success("Saved successfully");
};

onMounted(fetchCategories);
</script>

<style scoped>
/* pagination style */ 
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

/* Mini Pagination */
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Active page (highlight like the screenshot) */
.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
}
.page-active:hover{
   background: #21c1d6;
}
</style>
